import { Dispatch, SetStateAction, useState } from "react";
import AppDrawer from "../AppDrawer";
import BasicTableView from "../../../views/supportal/CloudListView/BasicTableView";
import useBulkEmails from "../../../api/api-hooks/bulkEmail/useBulkEmails";
import { Alert, Button, Input } from "reactstrap";
import IconButton from "../../../elements/IconButton";
import { findWhere } from "underscore";
import IconCard from "../../../views/workflows/IconCard";
import useSendBulkEmail from "../../../api/api-hooks/bulkEmail/useSendBulkEmail";
import AppSpinner from "../../../elements/AppSpinner";
import { AppConfirm } from "../../../utils/PopupUtil";

interface Props {
  open: boolean;
  onClose: () => void;
  recordID: string;
}

type DrawerPage =
  | "Select Email Action"
  | "Recipient List"
  | "Confirm Email Action";

export default function EmailActionsDrawer({ open, onClose, recordID }: Props) {
  const [page, setPage] = useState<DrawerPage>("Select Email Action");
  const [actionID, setActionID] = useState("");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [ccs, setCCs] = useState<string[]>([]);
  const [GEEventID, setGEEventID] = useState<string>();

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle={page}
      headerOptions={
        <>
          {page !== "Select Email Action" && (
            <Button
              size="sm"
              onClick={() =>
                setPage(
                  page === "Confirm Email Action"
                    ? "Recipient List"
                    : "Select Email Action"
                )
              }
            >
              Go Back
            </Button>
          )}
          {page === "Recipient List" && (
            <Button
              size="sm"
              color="primary"
              style={{ width: 80 }}
              onClick={() => setPage("Confirm Email Action")}
              disabled={!recipients?.length}
            >
              Next
            </Button>
          )}
        </>
      }
      body={
        <>
          {page === "Select Email Action" && (
            <SelectEmailAction
              setActionID={setActionID}
              setPage={setPage}
              setGEEventID={setGEEventID}
              GEEventID={GEEventID}
            />
          )}
          {page === "Recipient List" && (
            <RecipientList
              recipients={recipients}
              ccs={ccs}
              setRecipients={setRecipients}
              setCCs={setCCs}
            />
          )}
          {page === "Confirm Email Action" && (
            <ConfirmEmailAction
              recipients={recipients}
              ccs={ccs}
              actionID={actionID}
              recordID={recordID}
              GEEventID={GEEventID}
              handleClose={handleClose}
            />
          )}
        </>
      }
    />
  );

  function handleClose() {
    setActionID("");
    setRecipients([]);
    setCCs([]);
    setGEEventID("");
    setPage("Select Email Action");
    onClose();
  }
}

interface SelectActionProps {
  setGEEventID: any;
  GEEventID?: string;
  setActionID: Dispatch<SetStateAction<string>>;
  setPage: Dispatch<SetStateAction<DrawerPage>>;
}

// First page of EmailActionsDrawer. Allows user to select an email action (if any exist)
function SelectEmailAction({
  setGEEventID,
  GEEventID,
  setActionID,
  setPage,
}: SelectActionProps) {
  const { emailActions, emailActionsLoading } = useBulkEmails();

  return (
    <>
      <h4 className="mb-0 mt--3">(GrowthEvents) Event ID</h4>
      <div className="small mb-2">
        Enter an Event ID if you want to keep track of your invites.
      </div>
      <Input
        className="mb-3"
        value={GEEventID}
        onChange={(e) => setGEEventID(e.target.value)}
        placeholder="EVENT_2023"
        bsSize="sm"
      />
      <BasicTableView
        loading={emailActionsLoading}
        items={emailActions}
        title="Actions"
        getItemTitle={(e) => e?.Name}
        getItemSubtitle={(e) => e?.Description || `${e.Type} Email Template`}
        onClick={(e) => {
          setActionID(e?.id!);
          setPage("Recipient List");
        }}
      />
    </>
  );
}

export const emailRegex = /[\w._%+-]+@[\w.-]+\.[a-zA-Z]{2,4}/g;
export const emailValidationRegex =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

interface RecipientListProps {
  recipients: string[];
  ccs: string[];
  setRecipients: Dispatch<SetStateAction<string[]>>;
  setCCs: Dispatch<SetStateAction<string[]>>;
}

// Page in EmailActionsDrawer to select the recipients to email.
function RecipientList({
  recipients,
  ccs,
  setRecipients,
  setCCs,
}: RecipientListProps) {
  const [text, setText] = useState("");
  const [CCsText, setCCsText] = useState("");

  return (
    <>
      <h4 className="mb-0">Enter Recipient Emails</h4>
      <small>Enter as many comma separated emails as you'd like.</small>
      <Input
        type="textarea"
        className="mt-2"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="bob@gmail.com,bill@gmail.com"
      />
      <Button
        color="primary"
        style={{ width: 100 }}
        className="mt-3 mb-4"
        onClick={addRecipients}
      >
        Add
      </Button>
      <h4 className="mb-0">Enter CCs Emails</h4>
      <small>Enter as many comma separated emails as you'd like.</small>
      <Input
        type="textarea"
        className="mt-2"
        value={CCsText}
        onChange={(e) => setCCsText(e.target.value)}
        placeholder="bob@gmail.com,bill@gmail.com"
      />
      <Button
        color="primary"
        style={{ width: 100 }}
        className="mt-3 mb-4"
        onClick={addCCs}
      >
        Add
      </Button>
      <BasicTableView
        items={recipients}
        loading={false}
        title={"Recipients"}
        getItemTitle={(r) => r}
        getItemSubtitle={() => ""}
        emptyMessage="No Recipients have been added."
        containerCSS="mb-4"
        CustomRenderer={(r) => (
          <IconButton
            icon="close"
            onClick={() => {
              setRecipients((prev) => prev.filter((x) => x !== r));
            }}
            color="danger"
          />
        )}
      />
      <BasicTableView
        items={ccs}
        loading={false}
        title={"CCs"}
        getItemTitle={(r) => r}
        getItemSubtitle={() => ""}
        emptyMessage="No CCs have been added."
        CustomRenderer={(r) => (
          <IconButton
            icon="close"
            onClick={() => {
              setCCs((prev) => prev.filter((x) => x !== r));
            }}
            color="danger"
          />
        )}
      />
    </>
  );

  function addRecipients() {
    const emails = text.match(emailRegex);

    if (!emails) return;

    const validEmails = emails.filter((email) => {
      return emailValidationRegex.test(email);
    });

    setRecipients((prev) => [...new Set([...prev, ...validEmails])]);
    setText("");
  }

  function addCCs() {
    const emails = CCsText.match(emailRegex);

    if (!emails) return;

    const validEmails = emails.filter((email) => {
      return emailValidationRegex.test(email);
    });

    setCCs((prev) => [...new Set([...prev, ...validEmails])]);
    setCCsText("");
  }
}

interface ConfirmActionProps {
  actionID: string;
  recipients: string[];
  ccs: string[];
  recordID: string;
  GEEventID?: string;
  handleClose: () => void;
}

// Final page of EmailActionsDrawer. Allows user to review the action they are about to perform, and perform it.
function ConfirmEmailAction({
  actionID,
  recipients,
  ccs,
  recordID,
  GEEventID,
  handleClose,
}: ConfirmActionProps) {
  const { emailActions } = useBulkEmails();
  const { sendBulkEmail, sending } = useSendBulkEmail();
  const recipientsLength = recipients?.length;

  const action = findWhere(emailActions, { id: actionID });

  if (!action) return null;

  return (
    <>
      <Alert
        color="warning font-weight-500"
        style={{ fontSize: 16, marginBottom: "3rem" }}
      >
        {" "}
        You are about to send out a bulk email. Please review the following
        details. If you are unsure of anything, we don't recommend you perform
        this operation.{" "}
      </Alert>
      <h4>Selected Email Action</h4>
      {/* @ts-ignore */}
      <IconCard
        icon={"mdi mdi-email-fast-outline"}
        name={action?.Name}
        description={action?.Description || `${action?.Type} Email Template`}
      />
      <h4 className="mt-4">Recipients ({recipientsLength})</h4>
      <div className="text-sm bg-superlight p-2 rounded-sm border text-dark font-weight-500">
        {recipients.join(", ")}
      </div>
      {ccs.length > 0 && (
        <>
          <h4 className="mt-4">CCs</h4>
          <div className="text-sm bg-superlight p-2 rounded-sm border text-dark font-weight-500">
            {ccs.join(", ")}
          </div>
        </>
      )}
      <Button
        color="primary"
        className="mt-4"
        disabled={sending}
        style={{ width: 200 }}
        onClick={sendEmails}
      >
        {sending ? <AppSpinner color="white" /> : "Send Bulk Email"}
      </Button>
    </>
  );

  function sendEmails() {
    if (!action) return;
    sendBulkEmail({
      recipients: recipients,
      ccs: ccs,
      action: action,
      recordID: recordID,
      GEEventID: GEEventID,
      onSuccess: () => {
        handleClose();
        AppConfirm({
          title: "Success",
          text: "Successfully started bulk email operation!",
          icon: "success",
          showCancelButton: false,
          confirmButtonText: "OK",
          confirmButtonColor: "var(--primary)",
        });
      },
    });
  }
}
